// /managers/ConvoManager/MessageActions.js
import { getFirestore, writeBatch, doc, deleteDoc, collection, query, where, getDocs } from 'firebase/firestore';
import { getChatSummary } from '@/managers/ChatFeedManager/GetSummary';
import { generateId } from './ConvoHelpers';
import { authManager } from '@/managers/AuthManager';
import { timestampService } from '@/utils/timestampService';
import { addMessageBatch } from './ConvoHelpers';
import { apiManager } from '@/components/layout/RightPanel/shelbulaworkspaces/apiManager';

/**
 * Factory function to create message-specific actions.
 * @param {object} state - The ConversationState instance.
 * @param {object} actions - Core conversation actions for dependencies.
 * @returns {object} An object containing message actions.
 */
export const createMessageActions = (state, actions) => {
  const firestore = getFirestore();

  const generateAndInsertSummary = async () => {
    const conversationId = state.currentConversationId;
    const currentAuthUser = authManager.getCurrentUser();

    if (!conversationId || !currentAuthUser?.uid) {
      console.error("[MessageActions] generateAndInsertSummary failed: No active conversation or user.");
      return false;
    }
    
    const conversation = state.conversationsMeta[conversationId];
    if (!conversation) {
      console.error(`[MessageActions] generateAndInsertSummary failed: Conversation ${conversationId} not found.`);
      return false;
    }

    const summaryMessageId = generateId('summary');
    const platform = conversation.botPlatform;
    const model = conversation.botModel;

    const summaryTimestamp = await timestampService.getTimestamp();
    const optimisticMessage = {
      id: summaryMessageId,
      role: 'system',
      type: 'summary',
      content: '',
      rawContent: '',
      status: 'generating',
      isOptimistic: true,
      persisted: false,
      timestamp: new Date(summaryTimestamp.getTime() + 1),
      metadata: { platform, model },
    };
    state.addOptimisticMessage(conversationId, optimisticMessage);

    try {
      const summaryResult = await getChatSummary(
        { ...conversation, messages: state.messages[conversationId] || [] },
        currentAuthUser.uid,
        platform,
        model
      );
      
      const finalMessageData = {
        id: summaryMessageId,
        role: 'system',
        type: 'summary',
        rawContent: summaryResult.summary,
        content: summaryResult.summary,
        status: 'completed',
        timestamp: summaryTimestamp,
        persisted: true,
        isOptimistic: false,
        userId: currentAuthUser.uid,
        botId: conversation.botId,
        modelId: conversation.modelId,
        metadata: {
          platform: summaryResult.platform,
          model: summaryResult.model,
          summarizedMessageCount: summaryResult.messageCount,
        },
      };

      const batch = writeBatch(firestore);
      await addMessageBatch(firestore, currentAuthUser.uid, conversationId, finalMessageData, {}, batch);
      await batch.commit();

      state.updateOptimisticMessage(conversationId, summaryMessageId, finalMessageData);

      await actions.updateConversation(conversationId, (currentMeta) => ({
          ...currentMeta,
          lastSummary: {
              id: finalMessageData.id,
              content: finalMessageData.rawContent,
              platform: finalMessageData.metadata.platform,
              model: finalMessageData.metadata.model,
              createdAt: finalMessageData.timestamp,
              messageCount: finalMessageData.metadata.summarizedMessageCount
          },
          lastSummaryTimestamp: finalMessageData.timestamp,
      }));

      // Save to workspace brain if this conversation is workspace-scoped
      if (conversation.workspaceContextId) {
          try {
              const now = summaryTimestamp instanceof Date ? summaryTimestamp : new Date(summaryTimestamp);
              const datePart = now.toISOString().slice(0, 10); // YYYY-MM-DD
              const timePart = now.toISOString().slice(11, 19).replace(/:/g, '-'); // HH-MM-SS
              const fileName = `ChatSummaryManualExport_${datePart}_${timePart}.md`;
              const filePath = `.WorkspaceBrain/ManualSummaries/${fileName}`;
              await apiManager.saveFileContent(conversation.workspaceContextId, filePath, summaryResult.summary, `[Human Action] Manual chat summary export`);
          } catch (saveError) {
              console.warn('[MessageActions] Could not save summary to workspace brain:', saveError.message);
          }
      }

      return true;

    } catch (error) {
      console.error('[MessageActions] Error generating or storing summary:', error);
      state.updateOptimisticMessage(conversationId, summaryMessageId, {
        status: 'error',
        isOptimistic: false,
        errorDetails: { message: error.message || 'Failed to generate summary.' },
      });
      return false;
    }
  };
  
  const deleteMessage = async (messageId) => {
    const conversationId = state.currentConversationId;
    if (!conversationId) {
      console.error("[MessageActions] deleteMessage failed: No active conversation.");
      return false;
    }
    const conversation = state.conversationsMeta[conversationId];
    if (!conversation) {
      console.error(`[MessageActions] deleteMessage failed: Conversation ${conversationId} not found.`);
      return false;
    }

    const messages = state.messages[conversationId] || [];
    const messageIndex = messages.findIndex(m => m.id === messageId);
    if (messageIndex === -1) {
      console.error(`[MessageActions] deleteMessage failed: Message ${messageId} not found.`);
      return false;
    }

    try {
      const updatedMessages = messages.filter(m => m.id !== messageId);
      
      const batch = writeBatch(firestore);
      const userConvoRef = doc(firestore, 'users', conversation.userId, 'conversations', conversationId);
      
      const msgRef = doc(userConvoRef, 'messages', messageId);
      batch.delete(msgRef);

      const filesRef = collection(userConvoRef, 'files');
      const filesQuery = query(filesRef, where('messageId', '==', messageId));
      const filesSnapshot = await getDocs(filesQuery);
      filesSnapshot.docs.forEach(fileDoc => batch.delete(fileDoc.ref));

      const metaUpdates = {
        messageCount: Math.max(0, conversation.messageCount - 1),
      };

      if (updatedMessages.length > 0) {
        const newLastMessage = updatedMessages[updatedMessages.length - 1];
        metaUpdates.lastMessagePreview = {
          id: newLastMessage.id,
          role: newLastMessage.role,
          timestamp: newLastMessage.timestamp,
          status: newLastMessage.status,
        };
      } else {
        metaUpdates.lastMessagePreview = null;
      }
      batch.update(userConvoRef, metaUpdates);

      await batch.commit();

      state.setMessages(conversationId, updatedMessages);
      
      return true;

    } catch (error) {
      console.error('[MessageActions] Error deleting message:', error);
      return false;
    }
  };

  /**
   * REFACTORED FOR CLIENT-SIDE PERFORMANCE:
   * This function now fetches all file references in bulk before deleting,
   * avoiding the "N+1 query problem" where a database read was happening inside a loop.
   */
  const rewindToMessage = async (messageId) => {
    const conversationId = state.currentConversationId;
    if (!conversationId) {
       console.error("[MessageActions] rewindToMessage failed: No active conversation.");
       return false;
    }
    const conversation = state.conversationsMeta[conversationId];
    if (!conversation) {
       console.error(`[MessageActions] rewindToMessage failed: Conversation ${conversationId} not found.`);
       return false;
    }

    const messages = state.messages[conversationId] || [];
    const messageIndex = messages.findIndex(m => m.id === messageId);
    if (messageIndex === -1) {
        console.error(`[MessageActions] rewindToMessage failed: Message ${messageId} not found.`);
        return false;
    }

    const targetMessage = messages[messageIndex];
    // OPTIMIZATION: Get the full messages to delete, not just IDs
    const messagesToDelete = messages.slice(messageIndex);
    const messageIdsToDelete = messagesToDelete.map(m => m.id);

    try {
      const userConvoRef = doc(firestore, 'users', conversation.userId, 'conversations', conversationId);
      const filesRef = collection(userConvoRef, 'files');
      
      // OPTIMIZATION: Fetch all associated file references in bulk.
      const fileDocsToDelete = [];
      if (messageIdsToDelete.length > 0) {
        const idChunks = [];
        for (let i = 0; i < messageIdsToDelete.length; i += 30) {
          idChunks.push(messageIdsToDelete.slice(i, i + 30));
        }
        for (const chunk of idChunks) {
          const filesQuery = query(filesRef, where('messageId', 'in', chunk));
          const filesSnapshot = await getDocs(filesQuery);
          filesSnapshot.docs.forEach(fileDoc => fileDocsToDelete.push(fileDoc.ref));
        }
      }

      // Combine all document references to be deleted.
      const allDeletions = [
        ...messagesToDelete.map(msg => doc(userConvoRef, 'messages', msg.id)),
        ...fileDocsToDelete
      ];
      
      const updatedMessages = messages.slice(0, messageIndex);
      
      // Perform deletions in efficient batches.
      const MAX_BATCH_OPERATIONS = 495;
      let currentBatch = writeBatch(firestore);
      let opCount = 0;

      for (const docRef of allDeletions) {
        currentBatch.delete(docRef);
        opCount++;
        if (opCount >= MAX_BATCH_OPERATIONS) {
          await currentBatch.commit();
          currentBatch = writeBatch(firestore);
          opCount = 0;
        }
      }

      // Add final metadata update to the LAST batch.
      const metaUpdates = {
        messageCount: updatedMessages.length,
        status: 'idle',
      };
      if (updatedMessages.length > 0) {
        const newLastMessage = updatedMessages[updatedMessages.length - 1];
        metaUpdates.lastMessagePreview = {
          id: newLastMessage.id, role: newLastMessage.role,
          timestamp: newLastMessage.timestamp, status: newLastMessage.status,
        };
      } else {
        metaUpdates.lastMessagePreview = null;
      }
      currentBatch.update(userConvoRef, metaUpdates);
      
      // Commit the final batch.
      await currentBatch.commit();

      // Update state AFTER successful persistence.
      state.setMessages(conversationId, updatedMessages);
      
      const rewindPayload = {
          content: targetMessage.rawContent || '',
          files: targetMessage.files || [],
          images: targetMessage.images || [],
      };
      state.setRewoundMessagePayload(rewindPayload);

      return true;

    } catch (error) {
      console.error('[MessageActions] Error rewinding conversation:', error);
      return false;
    }
  };

  /**
   * REFACTORED FOR CLIENT-SIDE PERFORMANCE:
   * This implementation remains entirely on the client but optimizes the batching process
   * to reduce the number of network round trips to Firebase, which is the primary source of slowness.
   */
  const restartHistoryFromMessage = async (messageId, createSummary) => {
    const conversationId = state.currentConversationId;
    if (!conversationId) return false;
    
    const conversation = state.conversationsMeta[conversationId];
    if (!conversation) return false;

    const allMessages = state.messages[conversationId] || [];
    const messageIndex = allMessages.findIndex(msg => msg.id === messageId);
    if (messageIndex === -1) return false;

    const messagesToDelete = allMessages.slice(0, messageIndex);
    const remainingMessages = allMessages.slice(messageIndex);
    let summaryMessage = null;

    // Phase 1: Generate summary if needed
    if (createSummary && messagesToDelete.length > 0) {
        try {
            const summaryResult = await getChatSummary(
                { messages: messagesToDelete, id: conversation.id },
                conversation.userId,
          conversation.botPlatform,
          conversation.botModel
            );

            if (!summaryResult?.summary) throw new Error("Summary generation failed.");

            const firstKeptMessageTimestamp = remainingMessages[0]?.timestamp;
            summaryMessage = {
                id: generateId('summary'),
                role: 'assistant',
                type: 'context_summary',
                rawContent: summaryResult.summary,
                contentBlocks: [{ type: 'text', content: summaryResult.summary }],
                timestamp: firstKeptMessageTimestamp ? new Date(firstKeptMessageTimestamp.getTime() - 1) : new Date(),
                status: 'completed',
                persisted: true,
                userId: conversation.userId,
                botId: conversation.botId,
                modelId: conversation.modelId,
                metadata: { 
                  type: 'context_summary',
                  platform: summaryResult.platform,
                  model: summaryResult.model,
                  summarizedMessageCount: messagesToDelete.length,
                  replacesHistory: true,
                },
            };
        } catch (summaryError) {
            console.error('[MessageActions] Aborting restart: Could not generate summary.', summaryError);
            return false;
        }
    }

    try {
      const finalMessages = summaryMessage ? [summaryMessage, ...remainingMessages] : remainingMessages;
      const userConvoRef = doc(firestore, 'users', conversation.userId, 'conversations', conversationId);
      
      // Phase 2: Collect all documents to be deleted (messages and their files)
      const fileDocsToDelete = [];
      if (messagesToDelete.length > 0) {
        const messageIdsToDelete = messagesToDelete.map(msg => msg.id);
        const idChunks = [];
        for (let i = 0; i < messageIdsToDelete.length; i += 30) {
            idChunks.push(messageIdsToDelete.slice(i, i + 30));
        }
        
        const filesRef = collection(userConvoRef, 'files');
        for (const chunk of idChunks) {
            if (chunk.length === 0) continue;
            const filesQuery = query(filesRef, where('messageId', 'in', chunk));
            const filesSnapshot = await getDocs(filesQuery);
            filesSnapshot.docs.forEach(fileDoc => fileDocsToDelete.push(fileDoc.ref));
        }
      }

      // Phase 3: EFFICIENT BATCHING
      const MAX_BATCH_OPERATIONS = 495; // Leave headroom for final updates
      
      const allDeletions = [
          ...messagesToDelete.map(msg => doc(userConvoRef, 'messages', msg.id)),
          ...fileDocsToDelete
      ];
      
      if (allDeletions.length === 0 && !summaryMessage) {
        return true;
      }
      
      let currentBatch = writeBatch(firestore);
      let opCount = 0;

      for (let i = 0; i < allDeletions.length; i++) {
        currentBatch.delete(allDeletions[i]);
        opCount++;
        if (opCount >= MAX_BATCH_OPERATIONS) {
          await currentBatch.commit();
          currentBatch = writeBatch(firestore);
          opCount = 0;
        }
      }

      // Add the final operations to the LAST batch
      if (summaryMessage) {
        const summaryMsgRef = doc(userConvoRef, 'messages', summaryMessage.id);
        const finalSummaryData = await actions.formatMessageForStorage(summaryMessage, conversation.userId);
        currentBatch.set(summaryMsgRef, finalSummaryData);
      }

      const metaUpdates = { messageCount: finalMessages.length };
      if (finalMessages.length > 0) {
        const newLastMessage = finalMessages[finalMessages.length - 1];
        metaUpdates.lastMessagePreview = {
          id: newLastMessage.id,
          role: newLastMessage.role,
          timestamp: newLastMessage.timestamp,
          status: newLastMessage.status,
        };
      } else {
        metaUpdates.lastMessagePreview = null;
      }
      currentBatch.update(userConvoRef, metaUpdates);

      await currentBatch.commit();

      // Phase 4: Update local state AFTER all database operations succeed
      state.setMessages(conversationId, finalMessages);
      
      return true;
    } catch (error) {
      console.error('[MessageActions] Error during history restart persistence:', error);
      return false;
    }
  };

  const branchConversation = async (messageId, options = {}) => {
    const { newTab = false } = options;
    const parentConversationId = state.currentConversationId;
    const currentAuthUser = authManager.getCurrentUser();

    if (!parentConversationId || !currentAuthUser?.uid) return false;

    const parentConvo = state.conversationsMeta[parentConversationId];
    if (!parentConvo) return false;
    
    const allMessages = state.messages[parentConversationId] || [];
    const messageIndex = allMessages.findIndex(m => m.id === messageId);
    if (messageIndex === -1) return false;

    const messagesToClone = allMessages.slice(0, messageIndex + 1);

    try {
        const { success, conversation: newConvoShell } = await actions.createConversation({
            fromInitNoNav: true,
            botId: parentConvo.botId,
            modelId: parentConvo.modelId,
        });

        if (!success || !newConvoShell?.id) throw new Error("Branch failed: Could not create new conversation shell.");
        
        const newConversationId = newConvoShell.id;

        const clonedMessagesData = messagesToClone.map(messageToClone => {
            const newClonedMessageId = generateId('msg');
            return {
                originalMessage: messageToClone,
                newId: newClonedMessageId,
                attachments: {
                    files: messageToClone.files || [],
                    images: messageToClone.images || []
                },
                clonedData: {
                    ...messageToClone,
                    id: newClonedMessageId,
                    contentForUI: messageToClone.role === 'assistant' 
                        ? (messageToClone.contentForUI || messageToClone.streamEvents || []) 
                        : messageToClone.rawContent,
                },
                isTargetMessage: messageToClone.id === messageId,
            };
        });

        const batch = writeBatch(firestore);
        
        await Promise.all(
            clonedMessagesData.map(({ clonedData, attachments }) => 
                addMessageBatch(firestore, currentAuthUser.uid, newConversationId, clonedData, attachments, batch)
            )
        );

        const lastClonedData = clonedMessagesData.find(m => m.isTargetMessage) || clonedMessagesData[clonedMessagesData.length - 1];
        const lastClonedMessage = lastClonedData.clonedData;
        
        const newMeta = {
            name: `Branch of "${parentConvo.name}"`,
            status: 'idle',
          persisted: true,
          messagesLoaded: true,
          hasMoreMessages: false,
          oldestLoadedMessageTimestamp: messagesToClone.length > 0 ? messagesToClone[0].timestamp : null,
            messageCount: messagesToClone.length,
            lastMessagePreview: {
                id: lastClonedMessage.id,
                role: lastClonedMessage.role,
                status: lastClonedMessage.status,
                timestamp: lastClonedMessage.timestamp,
            },
        };
        const newConvoRef = doc(firestore, 'users', currentAuthUser.uid, 'conversations', newConversationId);
        batch.set(newConvoRef, { ...newConvoShell, ...newMeta }, { merge: true });

        await batch.commit();

        const clonedMessagesForState = clonedMessagesData.map(({ clonedData, attachments }) => ({
          ...clonedData,
          files: Array.isArray(attachments?.files) ? attachments.files : Object.values(attachments?.files || {}),
          images: Array.isArray(attachments?.images) ? attachments.images : [],
          persisted: true,
          isOptimistic: false,
          _pendingDecryption: false,
        }));

        state.setConversationMeta(newConversationId, newMeta);
        state.setMessages(newConversationId, clonedMessagesForState);

        await actions.switchToConversation(newConversationId, { newTab });
        
        return true;

    } catch (error) {
        console.error('[MessageActions] Error branching conversation:', error);
        return false;
    }
  };

  const removeAttachment = async (conversationId, messageId, attachmentId, attachmentType) => {
    const currentAuthUser = authManager.getCurrentUser();
    
    if (!currentAuthUser?.uid || !conversationId) {
      console.error("[MessageActions] removeAttachment failed: No active conversation or user.");
      return false;
    }
  
    const currentMessages = state.messages[conversationId] || [];
    const messageToUpdate = currentMessages.find(m => m.id === messageId);
  
    if (!messageToUpdate) {
      console.error(`[MessageActions] removeAttachment failed: Message ${messageId} not found in state.`);
      return false;
    }
  
    const originalFiles = [...(messageToUpdate.files || [])];
    const originalImages = [...(messageToUpdate.images || [])];
  
    let updatedFiles = [...originalFiles];
    let updatedImages = [...originalImages];
    let attachmentRemoved = false;
    let firestoreDocId = attachmentId;
  
    const matchesAttachment = (attachment) => {
      return attachment.id === attachmentId || 
             attachment.originalId === attachmentId ||
             attachment.attachmentId === attachmentId;
    };
  
    if (attachmentType === 'image') {
      const matchedImage = originalImages.find(matchesAttachment);
      
      if (matchedImage) {
        firestoreDocId = matchedImage.id || matchedImage.originalId || attachmentId;
        updatedImages = originalImages.filter(img => !matchesAttachment(img));
        attachmentRemoved = true;
      }
    } else if (attachmentType === 'file') {
      const matchedFile = originalFiles.find(matchesAttachment);
      
      if (matchedFile) {
        firestoreDocId = matchedFile.id || matchedFile.originalId || attachmentId;
        updatedFiles = originalFiles.filter(file => !matchesAttachment(file));
        attachmentRemoved = true;
      }
    }
  
    if (!attachmentRemoved) {
      console.warn(`[MessageActions] Attachment ${attachmentId} not found in message ${messageId}.`);
      return false;
    }
  
    state.updateOptimisticMessage(conversationId, messageId, {
      files: updatedFiles,
      images: updatedImages,
    });

    const hasContent = messageToUpdate.rawContent && messageToUpdate.rawContent.trim().length > 0;
    const hasRemainingAttachments = updatedFiles.length > 0 || updatedImages.length > 0;

    try {
      const batch = writeBatch(firestore);
      const userConvoRef = doc(firestore, 'users', currentAuthUser.uid, 'conversations', conversationId);
      
      const attachmentRef = doc(
        firestore, 'users', currentAuthUser.uid, 'conversations', 
        conversationId, 'files', firestoreDocId
      );
      batch.delete(attachmentRef);
      
      if (!hasContent && !hasRemainingAttachments) {
        const msgRef = doc(userConvoRef, 'messages', messageId);
        batch.delete(msgRef);
        
        const conversation = state.conversationsMeta[conversationId];
        if (conversation) {
          const remainingMessages = currentMessages.filter(m => m.id !== messageId);
          const metaUpdates = {
            messageCount: Math.max(0, (conversation.messageCount || 0) - 1),
          };
          
          if (remainingMessages.length > 0) {
            const newLastMessage = remainingMessages[remainingMessages.length - 1];
            metaUpdates.lastMessagePreview = {
              id: newLastMessage.id, role: newLastMessage.role,
              timestamp: newLastMessage.timestamp, status: newLastMessage.status,
            };
          } else {
            metaUpdates.lastMessagePreview = null;
          }
          batch.update(userConvoRef, metaUpdates);
        }
      }
      
      await batch.commit();
      
      if (!hasContent && !hasRemainingAttachments) {
        const remainingMessages = currentMessages.filter(m => m.id !== messageId);
        state.setMessages(conversationId, remainingMessages);
      }
      
      return true;
      
    } catch (error) {
      console.error(`[MessageActions] Failed to remove attachment from Firebase:`, error);
      
      state.updateOptimisticMessage(conversationId, messageId, {
        files: originalFiles,
        images: originalImages,
      });
      
      return false;
    }
  };

  /**
   * REFACTORED FOR CLIENT-SIDE PERFORMANCE:
   * This function now includes the final metadata update in the last batch of deletions,
   * avoiding the "redundant batch commit problem" and saving a network round trip.
   */
  const removeAllAttachments = async (conversationId) => {
    const currentAuthUser = authManager.getCurrentUser();
    if (!currentAuthUser?.uid || !conversationId) {
      console.error("[MessageActions] removeAllAttachments failed: No active conversation or user.");
      return false;
    }

    const currentMessages = state.messages[conversationId] || [];
    
    const attachmentsToRemove = [];
    const messagesWithAttachments = new Map();
    const originalMessageStates = new Map();
    
    for (const msg of currentMessages) {
      let hasAttachments = false;
      originalMessageStates.set(msg.id, {
        files: [...(msg.files || [])], images: [...(msg.images || [])],
      });
      
      if (msg.files?.length > 0) {
        for (const file of msg.files) {
          const firestoreId = file.id || file.originalId;
          if (firestoreId) {
            attachmentsToRemove.push({ id: firestoreId });
            hasAttachments = true;
          }
        }
      }
      if (msg.images?.length > 0) {
        for (const img of msg.images) {
          const firestoreId = img.id || img.originalId;
          if (firestoreId) {
            attachmentsToRemove.push({ id: firestoreId });
            hasAttachments = true;
          }
        }
      }
      if (hasAttachments) messagesWithAttachments.set(msg.id, msg);
    }

    if (attachmentsToRemove.length === 0) return true;

    const optimisticMessages = currentMessages.map(msg => {
      if (messagesWithAttachments.has(msg.id)) return { ...msg, files: [], images: [] };
      return msg;
    });
    
    const nonEmptyMessages = optimisticMessages.filter(msg => {
      const hasContent = msg.rawContent && msg.rawContent.trim().length > 0;
      const hasAttachments = (msg.files?.length > 0) || (msg.images?.length > 0);
      return hasContent || hasAttachments || msg.role !== 'user';
    });
    
    state.setMessages(conversationId, nonEmptyMessages);

    try {
      const MAX_BATCH_OPERATIONS = 495;
      const userConvoRef = doc(firestore, 'users', currentAuthUser.uid, 'conversations', conversationId);
      
      const allDeletions = [];
      for (const attachment of attachmentsToRemove) {
        allDeletions.push(doc(userConvoRef, 'files', attachment.id));
      }
      for (const msg of optimisticMessages) {
        if (messagesWithAttachments.has(msg.id)) {
          const hasContent = msg.rawContent && msg.rawContent.trim().length > 0;
          if (!hasContent && msg.role === 'user') {
            allDeletions.push(doc(userConvoRef, 'messages', msg.id));
          }
        }
      }
      
      let currentBatch = writeBatch(firestore);
      let opCount = 0;

      for (const docRef of allDeletions) {
          currentBatch.delete(docRef);
          opCount++;
          if (opCount >= MAX_BATCH_OPERATIONS) {
              await currentBatch.commit();
              currentBatch = writeBatch(firestore);
              opCount = 0;
          }
      }

      // OPTIMIZATION: Add metadata update to the final batch.
      const conversation = state.conversationsMeta[conversationId];
      if (conversation) {
        const metaUpdates = { messageCount: nonEmptyMessages.length };
        if (nonEmptyMessages.length > 0) {
          const newLastMessage = nonEmptyMessages[nonEmptyMessages.length - 1];
          metaUpdates.lastMessagePreview = {
            id: newLastMessage.id, role: newLastMessage.role,
            timestamp: newLastMessage.timestamp, status: newLastMessage.status,
          };
        } else {
          metaUpdates.lastMessagePreview = null;
        }
        currentBatch.update(userConvoRef, metaUpdates);
      }
      
      // Commit the final batch with deletions and the metadata update.
      await currentBatch.commit();

      return true;
      
    } catch (error) {
      console.error('[MessageActions] Error in removeAllAttachments:', error);
      
      const rolledBackMessages = currentMessages.map(msg => {
        const original = originalMessageStates.get(msg.id);
        if (original) return { ...msg, files: original.files, images: original.images };
        return msg;
      });
      state.setMessages(conversationId, rolledBackMessages);
      
      return false;
    }
  };

  const cancelInflightMessage = async (messageId) => {
    const conversationId = state.currentConversationId;
    if (!conversationId) {
        console.error("[MessageActions] cancelInflightMessage failed: No active conversation.");
        return false;
    }
    const conversation = state.conversationsMeta[conversationId];
    if (!conversation) {
        console.error(`[MessageActions] cancelInflightMessage failed: Conversation ${conversationId} not found.`);
        return false;
    }

    const messages = state.messages[conversationId] || [];
    const messageIndex = messages.findIndex(m => m.id === messageId);
    if (messageIndex === -1) {
        console.error(`[MessageActions] cancelInflightMessage failed: Message ${messageId} not found.`);
        return false;
    }
    const message = messages[messageIndex];

    if (message.role !== 'assistant' || message.status !== 'inflight') {
        console.warn(`[MessageActions] Attempted to cancel a message that is not in-flight.`, { id: messageId, status: message.status });
        return true;
    }

    try {
        const batch = writeBatch(firestore);
        const userConvoRef = doc(firestore, 'users', conversation.userId, 'conversations', conversationId);
        
        const msgRef = doc(userConvoRef, 'messages', messageId);
        batch.update(msgRef, { status: 'cancelled' });

        batch.update(userConvoRef, { status: 'idle' });

        await batch.commit();

        state.updateOptimisticMessage(conversationId, messageId, {
            status: 'cancelled',
            isOptimistic: false,
        });
        
        state.setConversationMeta(conversationId, { status: 'idle' });

        return true;

    } catch (error) {
        console.error('[MessageActions] Error cancelling message:', error);
        state.updateOptimisticMessage(conversationId, messageId, {
            status: 'error',
            isOptimistic: false,
            errorDetails: { message: `Failed to cancel: ${error.message}` },
        });
        return false;
    }
  };

  return {
    generateAndInsertSummary,
    deleteMessage,
    rewindToMessage,
    restartHistoryFromMessage,
    branchConversation,
    removeAttachment,
    removeAllAttachments,
    cancelInflightMessage,
  };
};